import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarCheck,
  Baby,
  Clock,
  Note,
  ArrowClockwise,
  Sparkle,
  MagnifyingGlass,
  ProhibitInset,
  X,
  ShieldWarning,
  Phone,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import ClearanceChip from "@/components/compliance/ClearanceChip";
import {
  ATTENDANCE_STATUSES,
  STATUS_META,
  formatTime,
  listChildrenWithReport,
  listRosterForTeacher,
  listLeftovers,
  formatLeftoverDate,
  markAttendance,
  stampPickup,
  todayStr,
} from "@/lib/attendance";
import {
  formatLocalTime,
  listClassroomNoticesForDate,
} from "@/lib/attendanceNotices";

/**
 * Teacher · /teacher/attendance — mobile-first roster.
 *
 * The morning workflow drives this screen: the teacher walks in with
 * the phone in one hand and tags each child as they show up. We optimise
 * for big tap targets, classroom grouping, fast filtering, and a forgiving
 * undo path for accidental taps.
 *
 * Notable choices:
 *   * Status buttons are 44px tall on mobile (h-11) — under WCAG AA's
 *     touch-target guidance and plenty for thumbs.
 *   * Sonner toast carries an "Undo" action when the row had a previous
 *     status — teacher RLS doesn't allow DELETE so we never restore to
 *     "unmarked"; only same-day status flips are undoable.
 *   * Per-classroom section headers + a search input (only visible when
 *     the roster is large) reduce scan time on multi-room days.
 *   * The pickup chip hides until the child has a check-in time —
 *     disabled grey buttons are a worse UX than the absence of one.
 */
export default function TeacherAttendance() {
  const { status, profile } = useAuthGate();
  const profileId = profile?.id;
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState({ classrooms: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [busyChild, setBusyChild] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [reportedIds, setReportedIds] = useState(new Set());
  const [noticesByChild, setNoticesByChild] = useState({});
  const [autofilling, setAutofilling] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  // "Yesterday's leftovers" — children with a prior-day check-in and no
  // check-out. Surfaced only when viewing today (most common workflow).
  const [leftovers, setLeftovers] = useState([]);
  const [leftoverDismissed, setLeftoverDismissed] = useState(false);
  const [leftoverBusy, setLeftoverBusy] = useState(null);
  // Module C — calm blocking dialog when org enforces attendance clearance.
  const [blockDialog, setBlockDialog] = useState(null); // { row, status, blockerSummary } | null

  const load = useCallback(async () => {
    if (status !== "ready") {
      if (status === "session_expired") setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const out = await listRosterForTeacher({ date, profileId });
      setData(out);
      if (date === todayStr()) {
        const ids = out.rows.map((r) => r.child_id);
        try {
          const reported = await listChildrenWithReport({ childIds: ids, date });
          setReportedIds(reported);
        } catch {
          setReportedIds(new Set());
        }
      } else {
        setReportedIds(new Set());
      }
      // Load parent-submitted notices for the same date, grouped by child.
      try {
        const classroomIds = (out.classrooms || [])
          .map((c) => c.classroom_id || c.id)
          .filter(Boolean);
        const all = (
          await Promise.all(
            classroomIds.map((cid) =>
              listClassroomNoticesForDate({ classroomId: cid, date }),
            ),
          )
        ).flat();
        const map = {};
        all.forEach((n) => {
          map[n.child_id] = n;
        });
        setNoticesByChild(map);
      } catch {
        setNoticesByChild({});
      }
    } catch (err) {
      toast.error(err.message || "Could not load roster");
    } finally {
      setLoading(false);
    }
  }, [date, profileId, status]);

  useEffect(() => { load(); }, [load]);

  // Fetch leftovers separately — only meaningful when viewing today.
  useEffect(() => {
    if (!profile?.id || date !== todayStr()) {
      setLeftovers([]);
      setLeftoverDismissed(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const rows = await listLeftovers({ today: date });
        if (alive) setLeftovers(rows);
      } catch {
        if (alive) setLeftovers([]);
      }
    })();
    return () => { alive = false; };
  }, [profile?.id, date]);

  const onResolveLeftover = async (l) => {
    setLeftoverBusy(l.id);
    try {
      await stampPickup({ childId: l.child_id, date: l.date });
      setLeftovers((rows) => rows.filter((r) => r.id !== l.id));
      const name = `${l.child?.first_name || ""} ${l.child?.last_name || ""}`.trim() || "Child";
      toast.success(`${name}: pick-up stamped for ${formatLeftoverDate(l.date)}`);
    } catch (err) {
      toast.error(err?.message || "Could not stamp pick-up");
    } finally {
      setLeftoverBusy(null);
    }
  };

  const counts = useMemo(() => {
    const acc = { total: data.rows.length, present: 0, late: 0, absent: 0, unmarked: 0 };
    for (const r of data.rows) {
      const s = r.attendance?.status;
      if (!s) acc.unmarked += 1;
      else if (s === "present") acc.present += 1;
      else if (s === "late") acc.late += 1;
      else if (s === "absent" || s === "excused") acc.absent += 1;
    }
    return acc;
  }, [data]);

  // Apply filter + search to the flat row list. Memoised so the per-classroom
  // grouping below operates on the already-filtered set.
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (q) {
        const name = `${r.child_first_name} ${r.child_last_name || ""}`.toLowerCase();
        if (!name.includes(q)) return false;
      }
      const s = r.attendance?.status;
      if (filter === "all") return true;
      if (filter === "unmarked") return !s;
      if (filter === "present") return s === "present";
      if (filter === "late") return s === "late";
      if (filter === "absent") return s === "absent" || s === "excused";
      return true;
    });
  }, [data.rows, filter, query]);

  // Group filtered rows by classroom so multi-room teachers can scan
  // by section. Preserves the original sort order (classroom name asc).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of visibleRows) {
      const key = r.classroom_id || "_";
      if (!map.has(key)) {
        map.set(key, { classroom_id: key, classroom_name: r.classroom_name, rows: [] });
      }
      map.get(key).rows.push(r);
    }
    // Per-group counts for the section header.
    for (const g of map.values()) {
      const totalForRoom = data.rows.filter((r) => r.classroom_id === g.classroom_id).length;
      const presentForRoom = data.rows.filter(
        (r) => r.classroom_id === g.classroom_id && r.attendance?.status === "present",
      ).length;
      g.total = totalForRoom;
      g.present = presentForRoom;
    }
    return Array.from(map.values());
  }, [visibleRows, data.rows]);

  /**
   * Mark a child's status. Captures the previous attendance state so we
   * can offer a Sonner "Undo" — but only if the row was already marked.
   * Teacher RLS forbids DELETE, so unmarked → marked is one-way for
   * teachers (admins can clear via the admin attendance page).
   */
  const onMark = async (row, status) => {
    setBusyChild(row.child_id);
    const prev = row.attendance;
    try {
      const updated = await markAttendance({
        childId: row.child_id,
        date,
        status,
        notes: row.attendance?.notes || null,
      });
      setData((p) => ({
        ...p,
        rows: p.rows.map((r) =>
          r.child_id === row.child_id ? { ...r, attendance: updated } : r,
        ),
      }));
      const childLabel = `${row.child_first_name}${row.child_last_name ? " " + row.child_last_name : ""}`;
      const msg = `${childLabel}: ${STATUS_META[status]?.label || status}`;
      // Undo is only meaningful when there was a previous status to revert
      // to. First-time marks just stay (a teacher can re-tap to override).
      if (prev?.status && prev.status !== status) {
        toast.success(msg, {
          action: {
            label: "Undo",
            onClick: () => onMark(row, prev.status),
          },
        });
      } else {
        toast.success(msg);
      }
    } catch (err) {
      if (err?.code === "P0403" || err?.kind === "compliance_block") {
        // Calm blocking dialog — no raw toast, no scary error. The
        // teacher sees the reason + a one-tap "Notify admin" hint.
        setBlockDialog({
          row,
          status,
          blockerSummary:
            err.blockerSummary ||
            (err.message || "").replace(/^Blocked by compliance:\s*/i, "") ||
            "required health records missing",
        });
      } else {
        toast.error(err.message || "Could not save");
      }
    } finally {
      setBusyChild(null);
    }
  };

  const onPickup = async (row) => {
    setBusyChild(row.child_id);
    try {
      const updated = await stampPickup({ childId: row.child_id, date });
      setData((p) => ({
        ...p,
        rows: p.rows.map((r) =>
          r.child_id === row.child_id ? { ...r, attendance: updated } : r,
        ),
      }));
      toast.success(`${row.child_first_name}: pick-up stamped`);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusyChild(null);
    }
  };

  const onSaveNotes = async (row) => {
    setBusyChild(row.child_id);
    try {
      const updated = await markAttendance({
        childId: row.child_id,
        date,
        status: row.attendance?.status || "present",
        notes: notesDraft,
      });
      setData((p) => ({
        ...p,
        rows: p.rows.map((r) =>
          r.child_id === row.child_id ? { ...r, attendance: updated } : r,
        ),
      }));
      setExpanded(null);
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err.message || "Could not save notes");
    } finally {
      setBusyChild(null);
    }
  };

  const autofillable = data.rows.filter(
    (r) => reportedIds.has(r.child_id) && !r.attendance,
  );

  const onAutofill = async () => {
    if (autofillable.length === 0) return;
    setAutofilling(true);
    const results = await Promise.allSettled(
      autofillable.map((row) =>
        markAttendance({ childId: row.child_id, date, status: "present" }),
      ),
    );
    const ok = results.filter((r) => r.status === "fulfilled");
    const fail = results.length - ok.length;
    const updatedById = new Map();
    for (let i = 0; i < results.length; i += 1) {
      const res = results[i];
      if (res.status === "fulfilled" && res.value) {
        updatedById.set(autofillable[i].child_id, res.value);
      }
    }
    setData((p) => ({
      ...p,
      rows: p.rows.map((r) =>
        updatedById.has(r.child_id)
          ? { ...r, attendance: updatedById.get(r.child_id) }
          : r,
      ),
    }));
    setAutofilling(false);
    if (fail === 0) toast.success(`Marked ${ok.length} present from today's reports`);
    else toast.error(`Marked ${ok.length} present · ${fail} failed`);
  };

  const showSearch = data.rows.length > 8;

  if (status === "session_expired") {
    return <SessionExpiredState scope="attendance" testId="teacher-attendance-session-expired" />;
  }

  return (
    <div data-testid="teacher-attendance" className="space-y-5">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Today
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Attendance
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Tap a status to mark — it saves instantly. Add notes if anything's
          worth flagging for the family.
        </p>
      </header>

      {/* Date picker + quick stats */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CalendarCheck size={20} weight="duotone" className="text-primary-hover" />
            <label htmlFor="teacher-attendance-date" className="sr-only">Attendance date</label>
            <input
              id="teacher-attendance-date"
              data-testid="teacher-attendance-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={todayStr()}
              className="rounded-xl border border-neutral-200 bg-white px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button
              type="button"
              data-testid="teacher-attendance-today"
              onClick={() => setDate(todayStr())}
              className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
            >
              Today
            </button>
          </div>
          <button
            type="button"
            data-testid="teacher-attendance-refresh"
            onClick={load}
            aria-label="Refresh roster"
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
          >
            <ArrowClockwise size={11} weight="bold" />
            Refresh
          </button>
        </div>

        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
          <Mini testId="teacher-attendance-count-total"    value={counts.total}    label="Total" />
          <Mini testId="teacher-attendance-count-present"  value={counts.present}  label="Present" accent />
          <Mini testId="teacher-attendance-count-late"     value={counts.late}     label="Late" />
          <Mini testId="teacher-attendance-count-unmarked" value={counts.unmarked} label="Unmarked" />
        </div>
      </section>

      {autofillable.length > 0 && (
        <section
          data-testid="teacher-attendance-autofill-banner"
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4"
        >
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white">
              <Sparkle size={14} weight="duotone" className="text-primary-hover" />
            </span>
            <div>
              <p className="font-heading text-sm font-semibold text-neutral-900">
                {autofillable.length} child{autofillable.length === 1 ? "" : "ren"} already have
                {" "}a daily report today
              </p>
              <p className="mt-0.5 text-xs text-neutral-600">
                You can mark them Present in one tap. Already-marked kids aren't changed.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="teacher-attendance-autofill-button"
            onClick={onAutofill}
            disabled={autofilling}
            className="inline-flex h-11 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:opacity-60 sm:h-9"
          >
            <Sparkle size={12} weight="bold" />
            {autofilling ? "Marking…" : "Autofill present"}
          </button>
        </section>
      )}

      {!leftoverDismissed && leftovers.length > 0 && date === todayStr() && (
        <section
          data-testid="teacher-attendance-leftovers"
          className="rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-4"
        >
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white">
              <Clock size={14} weight="duotone" className="text-[#C6851E]" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-semibold text-neutral-900">
                Pick-up wasn't recorded for {leftovers.length} {leftovers.length === 1 ? "child" : "children"}
              </p>
              <p className="mt-0.5 text-xs text-neutral-600">
                These rows from earlier days are missing a pick-up time. A quick stamp keeps the audit trail clean.
              </p>
              <ul className="mt-2.5 space-y-1.5">
                {leftovers.map((l) => {
                  const name = `${l.child?.first_name || ""} ${l.child?.last_name || ""}`.trim() || "Child";
                  return (
                    <li
                      key={l.id}
                      data-testid={`teacher-attendance-leftover-${l.id}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/60 bg-white px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-neutral-900">{name}</p>
                        <p className="truncate text-[11px] text-neutral-500">
                          {l.classroom?.name || ""}
                          {l.classroom?.name ? " · " : ""}
                          In {formatTime(l.check_in_at)} · {formatLeftoverDate(l.date)}
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={`teacher-attendance-leftover-${l.id}-stamp`}
                        onClick={() => onResolveLeftover(l)}
                        disabled={leftoverBusy === l.id}
                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#C6851E] px-3 text-xs font-semibold text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-60"
                      >
                        <Clock size={11} weight="bold" />
                        {leftoverBusy === l.id ? "Stamping…" : "Stamp pick-up"}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                data-testid="teacher-attendance-leftovers-dismiss"
                onClick={() => setLeftoverDismissed(true)}
                className="mt-2 text-[11px] font-semibold text-neutral-500 underline-offset-2 hover:text-neutral-700 hover:underline"
              >
                Dismiss for now
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Filter chips + search — only when roster is non-trivial */}
      {data.rows.length > 0 && (
        <section
          data-testid="teacher-attendance-filterbar"
          className="space-y-2"
        >
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterChip f="all"      label="All"      count={counts.total}    active={filter === "all"}      onClick={() => setFilter("all")} />
            <FilterChip f="unmarked" label="Unmarked" count={counts.unmarked} active={filter === "unmarked"} onClick={() => setFilter("unmarked")} />
            <FilterChip f="present"  label="Present"  count={counts.present}  active={filter === "present"}  onClick={() => setFilter("present")} />
            <FilterChip f="late"     label="Late"     count={counts.late}     active={filter === "late"}     onClick={() => setFilter("late")} />
            <FilterChip f="absent"   label="Absent"   count={counts.absent}   active={filter === "absent"}   onClick={() => setFilter("absent")} />
          </div>
          {showSearch && (
            <div className="relative">
              <MagnifyingGlass
                size={14}
                weight="bold"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              />
              <input
                data-testid="teacher-attendance-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a child by name"
                aria-label="Search roster by child name"
                className="h-11 w-full rounded-2xl border border-neutral-200 bg-white pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:h-10"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : data.rows.length === 0 ? (
        <div
          data-testid="teacher-attendance-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            No children in your assigned classrooms
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Ask an admin to assign you to a classroom from the Staff page.
          </p>
        </div>
      ) : visibleRows.length === 0 ? (
        <div
          data-testid="teacher-attendance-filter-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            No children match your filters
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Try a different filter or clear your search.
          </p>
        </div>
      ) : (
        <div data-testid="teacher-attendance-roster" className="space-y-5">
          {grouped.map((g) => (
            <section
              key={g.classroom_id}
              data-testid={`teacher-attendance-group-${g.classroom_id}`}
            >
              {grouped.length > 1 || data.classrooms.length > 1 ? (
                <header className="mb-2 flex items-baseline justify-between px-1">
                  <h2 className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
                    {g.classroom_name}
                  </h2>
                  <p
                    data-testid={`teacher-attendance-group-${g.classroom_id}-counts`}
                    className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500"
                  >
                    {g.present}/{g.total} present
                  </p>
                </header>
              ) : null}
              <ul className="space-y-2">
                {g.rows.map((row) => (
                  <RosterRow
                    key={row.child_id}
                    row={row}
                    isBusy={busyChild === row.child_id}
                    isExpanded={expanded === row.child_id}
                    notesDraft={notesDraft}
                    setNotesDraft={setNotesDraft}
                    setExpanded={setExpanded}
                    onMark={onMark}
                    onPickup={onPickup}
                    onSaveNotes={onSaveNotes}
                    hasReportToday={reportedIds.has(row.child_id)}
                    notice={noticesByChild[row.child_id]}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      {blockDialog && (
        <ComplianceBlockDialog
          dialog={blockDialog}
          onClose={() => setBlockDialog(null)}
        />
      )}
    </div>
  );
}

function FilterChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`teacher-attendance-filter-${label.toLowerCase()}`}
      data-active={active}
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-all ${
        active
          ? "border-primary bg-primary text-white shadow-sm"
          : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
      }`}
    >
      {label}
      <span
        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
          active ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function RosterRow({
  row, isBusy, isExpanded, notesDraft, setNotesDraft, setExpanded,
  onMark, onPickup, onSaveNotes, hasReportToday, notice,
}) {
  const status = row.attendance?.status;
  const chipMeta = status ? STATUS_META[status] : STATUS_META.unmarked;
  const childLabel = `${row.child_first_name}${row.child_last_name ? " " + row.child_last_name : ""}`;
  const hasCheckIn = !!row.attendance?.check_in_at;
  const hasNotes = !!row.attendance?.notes;

  return (
    <li
      data-testid={`teacher-attendance-row-${row.child_id}`}
      data-status={status || "unmarked"}
      className={`rounded-2xl border bg-white p-3 ${status ? "border-neutral-200" : "border-neutral-200 ring-1 ring-dashed ring-neutral-200"}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-secondary/30">
            <Baby size={16} weight="duotone" className="text-primary-hover" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading text-sm font-semibold text-neutral-900">
              {childLabel}
            </p>
            <p className="truncate text-[11px] text-neutral-500">
              {row.classroom_name}
              {row.attendance?.check_in_at
                ? ` · In ${formatTime(row.attendance.check_in_at)}`
                : ""}
              {row.attendance?.check_out_at
                ? ` · Out ${formatTime(row.attendance.check_out_at)}${
                    row.attendance?.pickup_authorization?.full_name
                      ? ` · by ${row.attendance.pickup_authorization.full_name}`
                      : row.attendance?.pickup_self
                        ? " · by parent"
                        : ""
                  }`
                : ""}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <span
                data-testid={`teacher-attendance-row-${row.child_id}-status`}
                className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chipMeta.tone}`}
              >
              {chipMeta.label}
              </span>
              <ClearanceChip
                childId={row.child_id}
                testIdPrefix="teacher-attendance-clearance"
                inline={true}
              />
              {hasReportToday && !status && (
                <span
                  data-testid={`teacher-attendance-row-${row.child_id}-report-hint`}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-hover"
                >
                  <Sparkle size={8} weight="duotone" />
                  Has report today
                </span>
              )}
              {notice && !status && (
                <span
                  data-testid={`teacher-attendance-row-${row.child_id}-notice`}
                  data-notice-type={notice.notice_type}
                  title={notice.reason || ""}
                  className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                    notice.notice_type === "absent"
                      ? "border-[#C6851E]/40 bg-[#C6851E]/5 text-[#8A5A12]"
                      : "border-amber-300 bg-amber-50 text-amber-800"
                  }`}
                >
                  {notice.notice_type === "absent" ? (
                    <ProhibitInset size={8} weight="duotone" />
                  ) : (
                    <Clock size={8} weight="duotone" />
                  )}
                  {notice.notice_type === "absent"
                    ? "Parent: absent"
                    : `Parent: late${notice.expected_arrival_time ? ` ${formatLocalTime(notice.expected_arrival_time)}` : ""}`}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {ATTENDANCE_STATUSES.filter((s) => s.value !== "left_early").map((s) => {
            const active = status === s.value;
            return (
              <button
                key={s.value}
                type="button"
                data-testid={`teacher-attendance-row-${row.child_id}-mark-${s.value}`}
                data-active={active}
                disabled={isBusy}
                onClick={() => onMark(row, s.value)}
                aria-label={`Mark ${childLabel} ${s.label}`}
                aria-pressed={active}
                title={s.label}
                className={`flex h-11 min-w-[44px] items-center justify-center rounded-full border px-2 text-sm font-bold transition-all sm:h-9 sm:min-w-[40px] sm:text-xs ${
                  active
                    ? s.value === "present"
                      ? "border-primary bg-primary text-white"
                      : s.value === "late"
                        ? "border-[#C6851E] bg-[#C6851E] text-white"
                        : "border-neutral-600 bg-neutral-600 text-white"
                    : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
                } disabled:opacity-60`}
              >
                {s.short}
              </button>
            );
          })}
          {/* Pickup chip — only meaningful once the child has checked in */}
          {hasCheckIn && (
            <button
              type="button"
              data-testid={`teacher-attendance-row-${row.child_id}-pickup`}
              onClick={() => onPickup(row)}
              disabled={isBusy}
              title="Stamp pick-up time"
              aria-label={`Stamp pick-up for ${childLabel}`}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 hover:bg-neutral-50 disabled:opacity-60 sm:h-9 sm:w-9"
            >
              <Clock size={14} weight="duotone" />
            </button>
          )}
          <button
            type="button"
            data-testid={`teacher-attendance-row-${row.child_id}-notes-toggle`}
            onClick={() => {
              if (isExpanded) {
                setExpanded(null);
              } else {
                setExpanded(row.child_id);
                setNotesDraft(row.attendance?.notes || "");
              }
            }}
            aria-expanded={isExpanded}
            aria-label={hasNotes ? `Edit notes for ${childLabel}` : `Add note for ${childLabel}`}
            title={hasNotes ? "Edit notes" : "Add note"}
            className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all sm:h-9 sm:w-9 ${
              hasNotes
                ? "border-primary/40 bg-primary/10 text-primary-hover"
                : "border-neutral-200 bg-white text-neutral-400 hover:bg-neutral-50"
            }`}
          >
            <Note size={14} weight="duotone" />
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <textarea
            data-testid={`teacher-attendance-row-${row.child_id}-notes-textarea`}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            rows={2}
            placeholder="Optional note for the family (e.g. arrived with a cough)"
            aria-label={`Note for ${childLabel}`}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setExpanded(null)}
              className="h-9 rounded-full border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid={`teacher-attendance-row-${row.child_id}-notes-save`}
              onClick={() => onSaveNotes(row)}
              disabled={isBusy}
              className="h-9 rounded-full bg-primary px-3 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              {isBusy ? "Saving…" : "Save note"}
            </button>
          </div>
        </div>
      )}
      {hasNotes && !isExpanded && (
        <p className="mt-2 border-t border-neutral-100 pt-2 text-xs italic text-neutral-600">
          “{row.attendance.notes}”
        </p>
      )}
    </li>
  );
}

function Mini({ value, label, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border px-2 py-2 ${accent ? "border-primary/30 bg-primary/5" : "border-neutral-200"}`}
    >
      <p className={`font-heading text-lg font-semibold ${accent ? "text-primary-hover" : "text-neutral-900"}`}>
        {value}
      </p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </p>
    </div>
  );
}


/**
 * Calm, teacher-safe dialog that renders when rpc_mark_attendance
 * refuses with P0403. Shows the blocker labels + next-step guidance
 * without exposing raw error text. Appears only when the org setting
 * `enforce_attendance_clearance` is ON.
 */
function ComplianceBlockDialog({ dialog, onClose }) {
  const { row, blockerSummary, status } = dialog;
  const childLabel = `${row.child_first_name}${row.child_last_name ? " " + row.child_last_name : ""}`;
  return (
    <div
      data-testid="teacher-attendance-block-dialog"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="attendance-block-title"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-start gap-3 bg-[#C6851E]/10 px-5 pt-5 pb-4">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#C6851E]/20">
            <ShieldWarning size={20} weight="duotone" className="text-[#8A5A12]" />
          </span>
          <div className="min-w-0 flex-1">
            <p
              id="attendance-block-title"
              className="font-heading text-lg font-semibold tracking-tight text-[#8A5A12]"
            >
              Check-in blocked
            </p>
            <p className="mt-0.5 text-sm text-[#8A5A12]/90">
              {childLabel} can't be marked {status} right now.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="teacher-attendance-block-dialog-close"
            aria-label="Close"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[#8A5A12] hover:bg-[#C6851E]/20"
          >
            <X size={16} weight="bold" />
          </button>
        </header>
        <div className="space-y-4 px-5 py-5 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Missing requirements
            </p>
            <p
              data-testid="teacher-attendance-block-dialog-blockers"
              className="mt-1 rounded-xl bg-neutral-50 px-3 py-2 font-semibold text-neutral-900"
            >
              {blockerSummary}
            </p>
          </div>
          <div className="space-y-2 text-neutral-700">
            <p className="font-semibold">What to do next:</p>
            <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-relaxed">
              <li>Ask the family to upload the missing record when they drop off.</li>
              <li>An admin can review and unblock check-in if the record is on file elsewhere.</li>
              <li>Until then, please hold the child at drop-off or contact the office.</li>
            </ol>
          </div>
          <div className="flex flex-col gap-2">
            <a
              href="tel:"
              data-testid="teacher-attendance-block-dialog-call-office"
              className="flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover"
            >
              <Phone size={14} weight="duotone" />
              Call office
            </a>
            <button
              type="button"
              onClick={onClose}
              data-testid="teacher-attendance-block-dialog-ok"
              className="rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
